/**
 * Shared Google Maps plumbing for the Live map and the Schedule map. The script is loaded by
 * useGooglePlacesScript (same browser key as address autocomplete); the project has no
 * @types/google.maps, so the API is used untyped, as in the rest of the app.
 */
import { useEffect, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GoogleAny = any;

export function google(): GoogleAny {
  return (window as GoogleAny).google;
}

/**
 * Advanced markers need a Map ID (Cloud console → Map management, project steddy-platform).
 * DEMO_MAP_ID is Google's testing ID: it works, but set VITE_GOOGLE_MAP_ID for production.
 */
export const MAP_ID: string = (import.meta.env.VITE_GOOGLE_MAP_ID as string | undefined)?.trim() || 'DEMO_MAP_ID';

/** "hsl(var(--tech-3))" → "hsl(28, 90%, 45%)": canvas-drawn shapes (circles, lines) can't read CSS variables. */
export function resolveColour(colour: string): string {
  const m = /var\((--[\w-]+)\)/.exec(colour);
  if (!m) return colour;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim();
  return raw ? `hsl(${raw.split(/\s+/).join(', ')})` : '#888';
}

/** Rough national centre and zoom when a tenant has no jobs or areas on the map yet. */
export function countryCentre(country: string | null | undefined): { center: { lat: number; lng: number }; zoom: number } {
  switch ((country ?? '').toUpperCase()) {
    case 'US': return { center: { lat: 39.8, lng: -98.6 }, zoom: 4 };
    case 'CA': return { center: { lat: 56.1, lng: -106.3 }, zoom: 3 };
    case 'GB': return { center: { lat: 54.0, lng: -2.5 }, zoom: 5 };
    case 'NZ': return { center: { lat: -41.0, lng: 174.0 }, zoom: 5 };
    case 'IE': return { center: { lat: 53.4, lng: -8.0 }, zoom: 6 };
    case 'ZA': return { center: { lat: -29.0, lng: 24.0 }, zoom: 5 };
    case 'IL': return { center: { lat: 31.4, lng: 35.0 }, zoom: 7 };
    default:   return { center: { lat: -25.3, lng: 133.8 }, zoom: 4 };
  }
}

// Google calls window.gm_authFailure when it refuses the key (bad key, referrer, or billing off).
let authFailed = false;
const authListeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  const previous = (window as GoogleAny).gm_authFailure;
  (window as GoogleAny).gm_authFailure = () => {
    authFailed = true;
    authListeners.forEach((fn) => fn());
    if (typeof previous === 'function') previous();
  };
}

/** True once Google has refused the Maps key in this page load. */
export function useMapsAuthFailed(): boolean {
  const [failed, setFailed] = useState(authFailed);
  useEffect(() => {
    if (authFailed) { setFailed(true); return; }
    const fn = () => setFailed(true);
    authListeners.add(fn);
    return () => { authListeners.delete(fn); };
  }, []);
  return failed;
}

/** Follows the app's light/dark class on <html>, so the map can match it. */
export function useIsDarkMode(): boolean {
  const read = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setDark(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

/** Up to two initials for a technician badge. */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
